import * as Yup from 'yup';
import { fixGoSlice } from '../helpers/fix-go-types';
import { AddressSchema } from './address';
import { StoreSalesStatsSchema } from './store-sales-stat';

const MilestoneFlagsSchema = Yup.object().shape({
  uploaded_data: Yup.boolean().required(),
  vendor_integrated: Yup.boolean().required(),
  sent_first_order: Yup.boolean().required(),
  received_first_order: Yup.boolean().required(),
  trial_converted: Yup.boolean().required(),
});

export type MilestoneFlags = Yup.InferType<typeof MilestoneFlagsSchema>;

const LabelSchema = Yup.object().shape({
  id: Yup.number().required(),
  label: Yup.string().required(),
  color: Yup.string().required(),
});

export type Label = Yup.InferType<typeof LabelSchema>;

export enum UserRole {
  Owner = 'owner',
  Manager = 'operator',
  Employee = 'employee',
  Demo = 'demo',
}

export const UserRoleLabel: Record<string, string> = {
  [UserRole.Owner]: 'Owner',
  [UserRole.Manager]: 'Manager',
  [UserRole.Employee]: 'Employee',
  [UserRole.Demo]: 'Demo',
};

const UserStoreRelationSchema = Yup.object().shape({
  email: Yup.string().email().required(),
  store_id: Yup.string().required(),
  relation: Yup.string().oneOf(Object.values(UserRole)).required(),
});

export type UserStoreRelation = Yup.InferType<typeof UserStoreRelationSchema>;

export const StoreSchema = Yup.object().shape({
  id: Yup.string().required(),
  product_key: Yup.string().required(),
  owner_first_name: Yup.string().required(),
  owner_last_name: Yup.string().required(),
  phone_number: Yup.string().defined(),
  billing_address: AddressSchema.required(),
  shipping_address: AddressSchema.required(),
  timezone: Yup.string()
    .transform((value) => value || undefined)
    .default(Intl.DateTimeFormat().resolvedOptions().timeZone),
  name: Yup.string().required(),
  enabled: Yup.boolean().required(),
  subscription_id: Yup.string().defined().default(''),

  milestones: MilestoneFlagsSchema.required(),
  trial_end: Yup.date().optional(),

  sales_stats: StoreSalesStatsSchema.required(),
  // Go serializes a nil `[]Label` (no omitempty) as `null`; fixGoSlice coerces
  // null → [] so a store with no labels isn't dropped by validation.
  labels: Yup.array().of(LabelSchema.required()).defined().transform(fixGoSlice),
  users: Yup.array().of(UserStoreRelationSchema.required()).required(),

  created_at: Yup.date().required(),
  updated_at: Yup.date().required(),
  last_uploaded_at: Yup.date().nullable().defined(),

  // Computed server-side from feature flags. Optional so an absent field
  // (old backend during deploy, or IndexedDB rehydration that bypasses Yup)
  // is undefined and isReceivingEnabled can fall back to the hardcoded list.
  features: Yup.object()
    .shape({
      web_receiving: Yup.boolean().default(false),
      order_edit_v2: Yup.boolean().default(false),
    })
    .optional(),
});

export type Store = Yup.InferType<typeof StoreSchema>;

export const isTestStore = (store?: Store) => {
  if (!store) {
    return false;
  }

  return store.product_key.startsWith('N2L8-RYD');
};

export const isMarysStore = (store?: Store) => {
  if (!store) {
    return false;
  }

  return store.product_key === '6T5K-K2Y7';
};

export const isJerniganStore = (store?: Store) => {
  if (!store) {
    return false;
  }

  return store.product_key === 'QO72-CO33' || store.product_key === '3TD8-4XGF';
};

export const isDemoStore = (store?: Store) => {
  if (!store) {
    return false;
  }

  return store.product_key === 'W8UO-EYR6';
};
